const WORLD_WIDTH = 390;
const WORLD_HEIGHT = 844;
const TAU = Math.PI * 2;
const CODE_SEQUENCE = [24, 88, 8];
const CODE_DIRECTIONS = [1, -1, 1];

const COLORS = {
  ink: "#050807",
  deep: "#07100e",
  green: "#0f2e25",
  emerald: "#1f8061",
  mint: "#9ce6c8",
  gold: "#d6a94d",
  paleGold: "#ffe4a0",
  brass: "#755527",
  steel: "#26332f",
  silver: "#9aa7a1",
  danger: "#e0564a"
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function mod(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function normalizeAngle(value) {
  let angle = value;
  while (angle > Math.PI) angle -= TAU;
  while (angle < -Math.PI) angle += TAU;
  return angle;
}

function easeOutCubic(value) {
  return 1 - Math.pow(1 - value, 3);
}

function easeInOutCubic(value) {
  return value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawText(ctx, text, x, y, options) {
  const opts = options || {};
  ctx.save();
  ctx.font = opts.font || "700 16px Arial, sans-serif";
  ctx.fillStyle = opts.color || "#ffffff";
  ctx.textAlign = opts.align || "center";
  ctx.textBaseline = opts.baseline || "middle";
  if (opts.shadow) {
    ctx.shadowColor = opts.shadow;
    ctx.shadowBlur = opts.shadowBlur || 12;
  }
  if (opts.letterSpacing) {
    const characters = String(text).split("");
    const widths = characters.map(
      character => ctx.measureText(character).width
    );
    const total =
      widths.reduce((sum, width) => sum + width, 0) +
      opts.letterSpacing * (characters.length - 1);
    let cursor = x - total / 2;
    characters.forEach((character, index) => {
      const width = widths[index];
      ctx.fillText(character, cursor + width / 2, y);
      cursor += width + opts.letterSpacing;
    });
  } else {
    ctx.fillText(text, x, y);
  }
  ctx.restore();
}

function radialGradient(ctx, x, y, radius, inner, outer) {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, inner);
  gradient.addColorStop(1, outer);
  return gradient;
}

function seededRandom(seedState) {
  seedState.value = (seedState.value * 1664525 + 1013904223) >>> 0;
  return seedState.value / 4294967296;
}

function createDust(seedState) {
  return Array.from({ length: 30 }, (_, index) => ({
    x: seededRandom(seedState) * WORLD_WIDTH,
    y: seededRandom(seedState) * WORLD_HEIGHT,
    radius: 0.4 + seededRandom(seedState) * 1.5,
    speed: 2 + seededRandom(seedState) * 8,
    phase: index * 0.73
  }));
}

function createAudioEngine() {
  let context = null;
  let lastTickAt = 0;

  function ensureContext() {
    if (context) {
      if (context.state === "suspended") context.resume();
      return context;
    }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    context = new AudioContext();
    return context;
  }

  function tone(frequency, duration, volume, type, delay) {
    const audioContext = ensureContext();
    if (!audioContext) return;
    const start = audioContext.currentTime + (delay || 0);
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = type || "sine";
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume || 0.025, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
  }

  return {
    unlock() {
      ensureContext();
    },
    tick() {
      const now = performance.now();
      if (now - lastTickAt < 26) return;
      lastTickAt = now;
      tone(960, 0.045, 0.012, "square");
    },
    code() {
      tone(420, 0.16, 0.035, "sine");
      tone(640, 0.2, 0.03, "sine", 0.08);
    },
    ready() {
      tone(280, 0.2, 0.04, "triangle");
      tone(560, 0.32, 0.05, "triangle", 0.12);
      tone(840, 0.4, 0.035, "sine", 0.22);
    },
    lever() {
      tone(110, 0.32, 0.07, "sawtooth");
      tone(72, 0.46, 0.055, "triangle", 0.12);
    },
    reward() {
      tone(392, 0.38, 0.045, "sine");
      tone(523, 0.42, 0.04, "sine", 0.1);
      tone(659, 0.56, 0.04, "sine", 0.2);
    },
    destroy() {
      if (context && typeof context.close === "function") context.close();
      context = null;
    }
  };
}

export function createVaultGame(canvas, options) {
  const opts = options || {};
  const ctx = canvas.getContext("2d");
  const audio = createAudioEngine();
  const randomState = { value: 888 };
  const dust = createDust(randomState);
  const state = {
    mode: "intro",
    elapsed: 0,
    dialAngle: 0,
    dialValue: 0,
    foundCodes: [],
    lastDirection: 0,
    lastTickValue: 0,
    codeFlash: 0,
    readyPulse: 0,
    leverProgress: 0,
    doorProgress: 0,
    rewardPulse: 0,
    draggingDial: false,
    draggingLever: false,
    previousPointerAngle: 0,
    pointerDown: false,
    particles: [],
    status: "แตะเพื่อเริ่มภารกิจ",
    reward: 88
  };

  const viewport = {
    width: WORLD_WIDTH,
    height: WORLD_HEIGHT,
    dpr: 1,
    scale: 1,
    offsetX: 0,
    offsetY: 0
  };

  let animationFrame = 0;
  let previousTimestamp = performance.now();
  let destroyed = false;

  function vibrate(pattern) {
    if (navigator.vibrate) navigator.vibrate(pattern);
  }

  function resize() {
    const bounds = canvas.getBoundingClientRect();
    viewport.width = Math.max(1, bounds.width || window.innerWidth);
    viewport.height = Math.max(1, bounds.height || window.innerHeight);
    viewport.dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(viewport.width * viewport.dpr);
    canvas.height = Math.round(viewport.height * viewport.dpr);
    viewport.scale = Math.min(
      viewport.width / WORLD_WIDTH,
      viewport.height / WORLD_HEIGHT
    );
    viewport.offsetX = (viewport.width - WORLD_WIDTH * viewport.scale) / 2;
    viewport.offsetY = (viewport.height - WORLD_HEIGHT * viewport.scale) / 2;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    render();
  }

  function toWorld(clientX, clientY) {
    const bounds = canvas.getBoundingClientRect();
    return {
      x:
        (clientX - bounds.left - viewport.offsetX) /
        Math.max(viewport.scale, 0.001),
      y:
        (clientY - bounds.top - viewport.offsetY) /
        Math.max(viewport.scale, 0.001)
    };
  }

  function pointInCircle(point, x, y, radius) {
    const dx = point.x - x;
    const dy = point.y - y;
    return dx * dx + dy * dy <= radius * radius;
  }

  function pointInRect(point, x, y, width, height) {
    return (
      point.x >= x &&
      point.x <= x + width &&
      point.y >= y &&
      point.y <= y + height
    );
  }

  function nextTarget() {
    return CODE_SEQUENCE[state.foundCodes.length];
  }

  function nextDirection() {
    return CODE_DIRECTIONS[state.foundCodes.length];
  }

  function directionLabel(direction) {
    return direction > 0 ? "ตามเข็มนาฬิกา" : "ทวนเข็มนาฬิกา";
  }

  function updateDialValue(deltaAngle) {
    if (state.mode !== "cracking") return;
    state.dialAngle += deltaAngle;
    state.lastDirection = deltaAngle >= 0 ? 1 : -1;
    const rawValue = mod(Math.round((state.dialAngle / TAU) * 100), 100);
    state.dialValue = rawValue;
    if (rawValue !== state.lastTickValue) {
      state.lastTickValue = rawValue;
      audio.tick();
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    checkCode();
  }

  function distanceBetweenCodes(a, b) {
    const direct = Math.abs(a - b);
    return Math.min(direct, 100 - direct);
  }

  function checkCode() {
    const target = nextTarget();
    if (typeof target !== "number") return;
    if (
      distanceBetweenCodes(state.dialValue, target) <= 1 &&
      state.lastDirection === nextDirection()
    ) {
      state.dialValue = target;
      state.foundCodes.push(target);
      state.codeFlash = 1;
      audio.code();
      vibrate([22, 30, 34]);
      if (state.foundCodes.length === CODE_SEQUENCE.length) {
        state.mode = "unlocked";
        state.status = "ปลดล็อกแล้ว — ดึงคันโยกลง";
        state.readyPulse = 1;
        state.draggingDial = false;
        audio.ready();
        vibrate([35, 45, 35, 45, 70]);
      } else {
        state.status = `รหัสถูกต้อง — หมุน${directionLabel(
          nextDirection()
        )}ไปที่ ${String(nextTarget()).padStart(2, "0")}`;
      }
    }
  }

  function startGame() {
    audio.unlock();
    state.mode = "cracking";
    state.elapsed = 0;
    state.dialAngle = 0;
    state.dialValue = 0;
    state.foundCodes = [];
    state.lastDirection = 0;
    state.leverProgress = 0;
    state.doorProgress = 0;
    state.rewardPulse = 0;
    state.particles = [];
    state.status = "หมุนตามเข็มนาฬิกาไปที่ 24";
    vibrate(18);
  }

  function resetGame() {
    state.mode = "intro";
    state.elapsed = 0;
    state.dialAngle = 0;
    state.dialValue = 0;
    state.foundCodes = [];
    state.lastDirection = 0;
    state.leverProgress = 0;
    state.doorProgress = 0;
    state.rewardPulse = 0;
    state.draggingDial = false;
    state.draggingLever = false;
    state.particles = [];
    state.status = "แตะเพื่อเริ่มภารกิจ";
  }

  function spawnParticles(count, originX, originY, palette) {
    for (let index = 0; index < count; index += 1) {
      const angle = seededRandom(randomState) * TAU;
      const speed = 24 + seededRandom(randomState) * 118;
      state.particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 26,
        life: 0.7 + seededRandom(randomState) * 1.5,
        maxLife: 2.2,
        size: 1.5 + seededRandom(randomState) * 4,
        color: palette[Math.floor(seededRandom(randomState) * palette.length)]
      });
    }
  }

  function beginOpening() {
    if (state.mode !== "unlocked") return;
    state.mode = "opening";
    state.draggingLever = false;
    state.leverProgress = 1;
    state.doorProgress = 0;
    state.status = "กำลังเปิด VAULT 888";
    audio.lever();
    vibrate([80, 30, 120]);
    spawnParticles(38, 195, 424, [COLORS.gold, COLORS.paleGold, COLORS.mint]);
  }

  function completeReward() {
    if (state.mode === "reward") return;
    state.mode = "reward";
    state.rewardPulse = 1;
    state.status = "พบรางวัลตัวอย่าง 88 เครดิต";
    audio.reward();
    vibrate([40, 40, 40, 40, 120]);
    spawnParticles(74, 195, 430, [
      "#fff4bd",
      COLORS.gold,
      "#66d9ac",
      "#ffffff"
    ]);
    if (typeof opts.onReward === "function") opts.onReward(state.reward);
  }

  function updateParticles(dt) {
    state.particles.forEach(particle => {
      particle.life -= dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vy += 34 * dt;
      particle.vx *= Math.pow(0.97, dt * 60);
    });
    state.particles = state.particles.filter(particle => particle.life > 0);
  }

  function update(dt) {
    const safeDelta = clamp(dt, 0, 0.05);
    state.elapsed += safeDelta;
    state.codeFlash = Math.max(0, state.codeFlash - safeDelta * 1.8);
    state.readyPulse = Math.max(0, state.readyPulse - safeDelta * 0.45);
    state.rewardPulse = Math.max(0, state.rewardPulse - safeDelta * 0.35);

    if (state.mode === "opening") {
      state.doorProgress = clamp(state.doorProgress + safeDelta / 2.15, 0, 1);
      if (state.doorProgress >= 1) completeReward();
    }

    updateParticles(safeDelta);
  }

  function drawBackdrop() {
    const background = ctx.createLinearGradient(0, 0, 0, WORLD_HEIGHT);
    background.addColorStop(0, "#0a1714");
    background.addColorStop(0.45, "#07100e");
    background.addColorStop(1, "#020504");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const light = ctx.createRadialGradient(195, 315, 5, 195, 360, 360);
    light.addColorStop(0, "rgba(34, 120, 91, 0.2)");
    light.addColorStop(0.42, "rgba(15, 70, 52, 0.09)");
    light.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = "#729c89";
    ctx.lineWidth = 0.6;
    for (let x = -WORLD_HEIGHT; x < WORLD_WIDTH; x += 22) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + WORLD_HEIGHT, WORLD_HEIGHT);
      ctx.stroke();
    }
    ctx.restore();

    ctx.save();
    for (let index = 0; index < 6; index += 1) {
      const y = 112 + index * 127;
      ctx.strokeStyle = "rgba(160, 186, 175, 0.07)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(WORLD_WIDTH, y);
      ctx.stroke();
      for (let x = 18; x < WORLD_WIDTH; x += 88) {
        ctx.fillStyle = "rgba(1, 4, 3, 0.72)";
        ctx.beginPath();
        ctx.arc(x + (index % 2) * 33, y, 2.4, 0, TAU);
        ctx.fill();
        ctx.fillStyle = "rgba(190, 214, 202, 0.13)";
        ctx.beginPath();
        ctx.arc(x + (index % 2) * 33 - 0.6, y - 0.7, 0.8, 0, TAU);
        ctx.fill();
      }
    }
    ctx.restore();

    dust.forEach(particle => {
      const y = mod(particle.y - state.elapsed * particle.speed, WORLD_HEIGHT);
      const shimmer = 0.18 + 0.16 * Math.sin(state.elapsed + particle.phase);
      ctx.fillStyle = `rgba(220, 240, 230, ${shimmer})`;
      ctx.beginPath();
      ctx.arc(particle.x, y, particle.radius, 0, TAU);
      ctx.fill();
    });

    const vignette = radialGradient(
      ctx,
      195,
      410,
      470,
      "rgba(0,0,0,0)",
      "rgba(0,0,0,0.86)"
    );
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  }

  function drawBrand() {
    ctx.save();
    ctx.strokeStyle = "rgba(220, 175, 82, 0.45)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(58, 52);
    ctx.lineTo(138, 52);
    ctx.moveTo(252, 52);
    ctx.lineTo(332, 52);
    ctx.stroke();

    ctx.fillStyle = COLORS.gold;
    ctx.beginPath();
    ctx.moveTo(195, 37);
    ctx.lineTo(205, 47);
    ctx.lineTo(195, 58);
    ctx.lineTo(185, 47);
    ctx.closePath();
    ctx.fill();

    drawText(ctx, "UFAEASY", 195, 78, {
      font: "700 10px Arial, sans-serif",
      color: "#adc9bd",
      letterSpacing: 4
    });
    drawText(ctx, "VAULT 888", 195, 103, {
      font: "900 27px Arial Black, Arial, sans-serif",
      color: COLORS.paleGold,
      shadow: "rgba(214,169,77,.34)",
      shadowBlur: 20,
      letterSpacing: 1.3
    });
    ctx.restore();
  }

  function drawCodeRail() {
    const pulse = 0.5 + 0.5 * Math.sin(state.elapsed * 4);
    drawText(ctx, "SECURITY SEQUENCE", 195, 133, {
      font: "700 8px Arial, sans-serif",
      color: "rgba(180, 208, 196, .62)",
      letterSpacing: 2.6
    });

    CODE_SEQUENCE.forEach((code, index) => {
      const x = 103 + index * 92;
      const solved = index < state.foundCodes.length;
      const active =
        state.mode === "cracking" && index === state.foundCodes.length;
      const glow = active ? 0.35 + pulse * 0.3 : solved ? 0.42 : 0.08;

      ctx.save();
      ctx.shadowColor = solved ? COLORS.mint : COLORS.gold;
      ctx.shadowBlur = 18 * glow;
      roundRect(ctx, x - 31, 146, 62, 49, 10);
      const slotGradient = ctx.createLinearGradient(0, 146, 0, 195);
      slotGradient.addColorStop(
        0,
        solved ? "rgba(44,132,101,.48)" : "rgba(25,37,33,.9)"
      );
      slotGradient.addColorStop(1, "rgba(4,9,8,.96)");
      ctx.fillStyle = slotGradient;
      ctx.fill();
      ctx.lineWidth = active ? 1.6 : 1;
      ctx.strokeStyle = solved
        ? "rgba(156,230,200,.75)"
        : active
        ? "rgba(255,212,116,.88)"
        : "rgba(176,194,186,.18)";
      ctx.stroke();
      ctx.restore();

      drawText(ctx, CODE_DIRECTIONS[index] > 0 ? "↻" : "↺", x, 158, {
        font: "700 11px Arial, sans-serif",
        color: solved ? COLORS.mint : active ? COLORS.gold : "#66736e"
      });
      drawText(
        ctx,
        solved ? String(code).padStart(2, "0") : active ? "••" : "—",
        x,
        178,
        {
          font: "900 19px Arial Black, Arial, sans-serif",
          color: solved ? "#d9fff0" : active ? COLORS.paleGold : "#56615c",
          shadow: solved ? "rgba(106,233,181,.45)" : null
        }
      );
    });
  }

  function drawVaultInterior(visibility) {
    if (visibility <= 0) return;
    ctx.save();
    ctx.globalAlpha = clamp(visibility, 0, 1);

    const glow = radialGradient(
      ctx,
      195,
      416,
      176,
      "rgba(255,244,183,.98)",
      "rgba(36,125,91,.28)"
    );
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(195, 416, 168, 0, TAU);
    ctx.fill();

    const dark = ctx.createRadialGradient(195, 412, 20, 195, 412, 154);
    dark.addColorStop(0, "#193b2e");
    dark.addColorStop(0.76, "#0a1713");
    dark.addColorStop(1, "#020706");
    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.arc(195, 416, 144, 0, TAU);
    ctx.fill();

    for (let row = 0; row < 3; row += 1) {
      const y = 362 + row * 53;
      ctx.fillStyle = "rgba(183, 143, 65, .44)";
      roundRect(ctx, 94, y + 27, 202, 9, 4);
      ctx.fill();
      ctx.fillStyle = "rgba(255, 218, 128, .22)";
      ctx.fillRect(99, y + 28, 192, 1);
      for (let col = 0; col < 6; col += 1) {
        const x = 114 + col * 31 + (row % 2) * 5;
        ctx.fillStyle = col % 2 ? "#d7a746" : "#efc967";
        ctx.beginPath();
        ctx.ellipse(x, y + 24, 11, 5, -0.08, 0, TAU);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,244,185,.7)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    const beam = ctx.createLinearGradient(0, 245, 0, 550);
    beam.addColorStop(0, "rgba(255,250,205,0)");
    beam.addColorStop(0.45, "rgba(255,250,205,.16)");
    beam.addColorStop(1, "rgba(255,250,205,0)");
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.moveTo(161, 265);
    ctx.lineTo(229, 265);
    ctx.lineTo(275, 550);
    ctx.lineTo(115, 550);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  function drawDoorBolts(centerX, centerY, radius) {
    for (let index = 0; index < 12; index += 1) {
      const angle = (index / 12) * TAU;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      const bolt = ctx.createRadialGradient(x - 2, y - 2, 1, x, y, 9);
      bolt.addColorStop(0, "#c6d1cc");
      bolt.addColorStop(0.35, "#65746e");
      bolt.addColorStop(1, "#111a17");
      ctx.fillStyle = bolt;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, TAU);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,.75)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.strokeStyle = "rgba(225,236,231,.3)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 3, y - 3);
      ctx.lineTo(x + 3, y + 3);
      ctx.stroke();
    }
  }

  function drawDial(centerX, centerY) {
    const outer = ctx.createRadialGradient(
      centerX - 22,
      centerY - 24,
      8,
      centerX,
      centerY,
      105
    );
    outer.addColorStop(0, "#66746f");
    outer.addColorStop(0.43, "#283632");
    outer.addColorStop(0.78, "#111b18");
    outer.addColorStop(1, "#050a08");
    ctx.fillStyle = outer;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 102, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = "rgba(236,244,239,.28)";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(state.dialAngle);
    for (let index = 0; index < 100; index += 1) {
      const angle = (index / 100) * TAU - Math.PI / 2;
      const major = index % 10 === 0;
      const medium = index % 5 === 0;
      const innerRadius = major ? 73 : medium ? 79 : 83;
      const outerRadius = 93;
      ctx.strokeStyle = major
        ? COLORS.paleGold
        : medium
        ? "#9fb0a8"
        : "#5f6d67";
      ctx.lineWidth = major ? 2.2 : medium ? 1.4 : 0.75;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * innerRadius, Math.sin(angle) * innerRadius);
      ctx.lineTo(Math.cos(angle) * outerRadius, Math.sin(angle) * outerRadius);
      ctx.stroke();
      if (major) {
        const numberX = Math.cos(angle) * 61;
        const numberY = Math.sin(angle) * 61;
        ctx.save();
        ctx.translate(numberX, numberY);
        ctx.rotate(-state.dialAngle);
        drawText(ctx, String(index).padStart(2, "0"), 0, 0, {
          font: "700 9px Arial, sans-serif",
          color: "#d7dfdb"
        });
        ctx.restore();
      }
    }

    ctx.strokeStyle = "rgba(183,212,198,.22)";
    ctx.lineWidth = 10;
    for (let index = 0; index < 3; index += 1) {
      const angle = index * (TAU / 3);
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * 16, Math.sin(angle) * 16);
      ctx.lineTo(Math.cos(angle) * 48, Math.sin(angle) * 48);
      ctx.stroke();
    }
    ctx.restore();

    const hub = ctx.createRadialGradient(
      centerX - 8,
      centerY - 9,
      2,
      centerX,
      centerY,
      31
    );
    hub.addColorStop(0, "#94a69d");
    hub.addColorStop(0.45, "#35443e");
    hub.addColorStop(1, "#0b1210");
    ctx.fillStyle = hub;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 29, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = "#101816";
    ctx.lineWidth = 4;
    ctx.stroke();
    drawText(ctx, String(state.dialValue).padStart(2, "0"), centerX, centerY, {
      font: "900 17px Arial Black, Arial, sans-serif",
      color: state.codeFlash > 0 ? "#ffffff" : COLORS.paleGold,
      shadow: state.codeFlash > 0 ? COLORS.mint : "rgba(214,169,77,.3)",
      shadowBlur: 18
    });

    ctx.save();
    ctx.shadowColor = COLORS.gold;
    ctx.shadowBlur = 12;
    ctx.fillStyle = COLORS.paleGold;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - 118);
    ctx.lineTo(centerX - 9, centerY - 101);
    ctx.lineTo(centerX + 9, centerY - 101);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  function drawLever(baseX, baseY) {
    const active = state.mode === "unlocked";
    const glow = active ? 0.55 + 0.45 * Math.sin(state.elapsed * 5) : 0;
    const handleY = baseY - 86 + state.leverProgress * 155;
    const handleX = baseX + state.leverProgress * 4;

    ctx.save();
    if (active) {
      ctx.shadowColor = COLORS.gold;
      ctx.shadowBlur = 22 + glow * 18;
    }
    ctx.fillStyle = "#0d1714";
    roundRect(ctx, baseX - 25, baseY - 120, 50, 218, 24);
    ctx.fill();
    ctx.strokeStyle = active
      ? `rgba(255,218,128,${0.55 + glow * 0.35})`
      : "rgba(145,159,153,.3)";
    ctx.lineWidth = 2;
    ctx.stroke();

    const slot = ctx.createLinearGradient(baseX - 8, 0, baseX + 8, 0);
    slot.addColorStop(0, "#050908");
    slot.addColorStop(0.5, "#1e2925");
    slot.addColorStop(1, "#050908");
    ctx.fillStyle = slot;
    roundRect(ctx, baseX - 8, baseY - 92, 16, 158, 8);
    ctx.fill();

    ctx.strokeStyle = active ? "#c89a43" : "#59655f";
    ctx.lineWidth = 8;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(baseX, baseY + 66);
    ctx.lineTo(handleX, handleY);
    ctx.stroke();

    const grip = ctx.createLinearGradient(
      handleX - 16,
      handleY,
      handleX + 16,
      handleY
    );
    grip.addColorStop(0, active ? "#62421d" : "#242d29");
    grip.addColorStop(0.5, active ? "#f1c45d" : "#76837d");
    grip.addColorStop(1, active ? "#5a3a16" : "#202824");
    ctx.fillStyle = grip;
    roundRect(ctx, handleX - 18, handleY - 25, 36, 50, 16);
    ctx.fill();
    ctx.strokeStyle = active ? COLORS.paleGold : "#1c2521";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    drawText(ctx, active ? "PULL" : "LOCKED", baseX, baseY + 116, {
      font: "700 8px Arial, sans-serif",
      color: active ? COLORS.paleGold : "#59645f",
      letterSpacing: 1.2
    });
  }

  function drawDoor() {
    const opening = state.mode === "opening" || state.mode === "reward";
    const progress = opening ? easeInOutCubic(state.doorProgress) : 0;
    const centerX = 195 - progress * 286;
    const centerY = 424 + Math.sin(progress * Math.PI) * 5;
    const rotation = -progress * 0.11;
    const reveal = opening ? easeOutCubic(state.doorProgress) : 0;

    drawVaultInterior(reveal);

    ctx.save();
    if (state.mode === "opening" && state.doorProgress < 0.18) {
      const shake = (1 - state.doorProgress / 0.18) * 2.6;
      ctx.translate(Math.sin(state.elapsed * 92) * shake, 0);
    }
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    ctx.translate(-centerX, -centerY);

    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,.92)";
    ctx.shadowBlur = 38;
    ctx.shadowOffsetY = 18;
    const outer = ctx.createRadialGradient(
      centerX - 58,
      centerY - 62,
      30,
      centerX,
      centerY,
      194
    );
    outer.addColorStop(0, "#5d6c66");
    outer.addColorStop(0.34, "#33413c");
    outer.addColorStop(0.7, "#19231f");
    outer.addColorStop(1, "#070c0a");
    ctx.fillStyle = outer;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 186, 0, TAU);
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = "#090e0c";
    ctx.lineWidth = 15;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 174, 0, TAU);
    ctx.stroke();
    ctx.strokeStyle = "rgba(190,205,198,.24)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 166, 0, TAU);
    ctx.stroke();

    const plate = ctx.createRadialGradient(
      centerX - 38,
      centerY - 46,
      5,
      centerX,
      centerY,
      156
    );
    plate.addColorStop(0, "#65736e");
    plate.addColorStop(0.24, "#33413c");
    plate.addColorStop(0.64, "#1c2823");
    plate.addColorStop(1, "#0c1310");
    ctx.fillStyle = plate;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 151, 0, TAU);
    ctx.fill();

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(-0.12);
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = "#d2e2da";
    ctx.beginPath();
    ctx.ellipse(-44, -70, 83, 29, -0.55, 0, TAU);
    ctx.fill();
    ctx.restore();

    drawDoorBolts(centerX, centerY, 132);
    drawDial(centerX, centerY);
    if (progress < 0.42) drawLever(centerX + 151, centerY + 28);
    ctx.restore();
  }

  function drawParticles() {
    ctx.save();
    state.particles.forEach(particle => {
      const alpha = clamp(particle.life / particle.maxLife, 0, 1);
      ctx.globalAlpha = alpha;
      ctx.shadowColor = particle.color;
      ctx.shadowBlur = 9;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, TAU);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawInstruction() {
    if (state.mode === "intro" || state.mode === "reward") return;
    const activeGold = state.mode === "unlocked";
    ctx.save();
    const y = 668;
    ctx.fillStyle = "rgba(3, 8, 6, .76)";
    roundRect(ctx, 32, y, 326, 83, 21);
    ctx.fill();
    ctx.strokeStyle = activeGold
      ? "rgba(255,216,124,.5)"
      : "rgba(135,190,166,.24)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const iconX = 68;
    ctx.fillStyle = activeGold
      ? "rgba(214,169,77,.16)"
      : "rgba(58,147,111,.16)";
    ctx.beginPath();
    ctx.arc(iconX, y + 41, 23, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = activeGold ? COLORS.gold : COLORS.emerald;
    ctx.stroke();
    drawText(ctx, state.mode === "unlocked" ? "↓" : "↻", iconX, y + 41, {
      font: "900 22px Arial, sans-serif",
      color: activeGold ? COLORS.paleGold : COLORS.mint
    });

    drawText(
      ctx,
      state.mode === "unlocked" ? "ดึงคันโยกลงให้สุด" : state.status,
      102,
      y + 30,
      {
        font: "700 13px Arial, sans-serif",
        color: "#eff8f4",
        align: "left"
      }
    );
    drawText(
      ctx,
      state.mode === "unlocked"
        ? "ประตูพร้อมเปิดแล้ว"
        : "ลากวงล้อช้า ๆ ให้ตรงกับรหัส",
      102,
      y + 53,
      {
        font: "400 10px Arial, sans-serif",
        color: "rgba(190,211,202,.67)",
        align: "left"
      }
    );
    ctx.restore();
  }

  function drawPreviewTag() {
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,.42)";
    roundRect(ctx, 115, 781, 160, 26, 13);
    ctx.fill();
    drawText(ctx, "INTERACTIVE PREVIEW · NO REAL REWARD", 195, 794, {
      font: "700 6.5px Arial, sans-serif",
      color: "rgba(184,204,195,.65)",
      letterSpacing: 0.8
    });
    ctx.restore();
  }

  function drawIntro() {
    ctx.save();
    ctx.fillStyle = "rgba(1,4,3,.62)";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const spotlight = radialGradient(
      ctx,
      195,
      390,
      290,
      "rgba(21,91,67,.12)",
      "rgba(0,0,0,.85)"
    );
    ctx.fillStyle = spotlight;
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    drawText(ctx, "ภารกิจลับคืนนี้", 195, 270, {
      font: "700 11px Arial, sans-serif",
      color: COLORS.mint,
      letterSpacing: 2
    });
    drawText(ctx, "เปิดตู้เซฟ", 195, 316, {
      font: "900 36px Arial Black, Arial, sans-serif",
      color: "#ffffff",
      shadow: "rgba(77,194,149,.34)",
      shadowBlur: 22
    });
    drawText(ctx, "VAULT 888", 195, 356, {
      font: "900 30px Arial Black, Arial, sans-serif",
      color: COLORS.paleGold,
      shadow: "rgba(214,169,77,.42)",
      shadowBlur: 24,
      letterSpacing: 1
    });
    drawText(ctx, "หมุนรหัสทั้ง 3 ตัว แล้วดึงคันโยก", 195, 397, {
      font: "400 13px Arial, sans-serif",
      color: "rgba(229,239,235,.82)"
    });
    drawText(ctx, "เพื่อค้นหาสิ่งที่ซ่อนอยู่ด้านใน", 195, 419, {
      font: "400 13px Arial, sans-serif",
      color: "rgba(229,239,235,.82)"
    });

    const buttonGlow = 0.66 + Math.sin(state.elapsed * 3) * 0.14;
    ctx.save();
    ctx.shadowColor = COLORS.gold;
    ctx.shadowBlur = 22 * buttonGlow;
    const button = ctx.createLinearGradient(0, 628, 0, 692);
    button.addColorStop(0, "#f1ca70");
    button.addColorStop(0.5, "#c99337");
    button.addColorStop(1, "#8f6227");
    ctx.fillStyle = button;
    roundRect(ctx, 61, 625, 268, 66, 20);
    ctx.fill();
    ctx.strokeStyle = "#ffe5a1";
    ctx.lineWidth = 1.3;
    ctx.stroke();
    ctx.restore();
    drawText(ctx, "เริ่มภารกิจ", 195, 650, {
      font: "900 17px Arial Black, Arial, sans-serif",
      color: "#161006"
    });
    drawText(ctx, "TAP TO UNLOCK", 195, 673, {
      font: "700 7px Arial, sans-serif",
      color: "rgba(42,28,7,.72)",
      letterSpacing: 2
    });
    drawText(ctx, "ใช้การแตะและลาก · เปิดเสียงเพื่อสัมผัสเอฟเฟกต์", 195, 724, {
      font: "400 9px Arial, sans-serif",
      color: "rgba(182,201,192,.62)"
    });
    ctx.restore();
  }

  function drawOpeningStatus() {
    if (state.mode !== "opening") return;
    const progress = Math.round(state.doorProgress * 100);
    drawText(ctx, "ACCESS GRANTED", 195, 642, {
      font: "900 12px Arial Black, Arial, sans-serif",
      color: COLORS.mint,
      shadow: "rgba(106,233,181,.6)",
      shadowBlur: 16,
      letterSpacing: 2.2
    });
    ctx.fillStyle = "rgba(255,255,255,.13)";
    roundRect(ctx, 87, 670, 216, 5, 3);
    ctx.fill();
    const loading = ctx.createLinearGradient(87, 0, 303, 0);
    loading.addColorStop(0, COLORS.emerald);
    loading.addColorStop(1, COLORS.paleGold);
    ctx.fillStyle = loading;
    roundRect(ctx, 87, 670, 216 * state.doorProgress, 5, 3);
    ctx.fill();
    drawText(ctx, `${progress}%`, 195, 697, {
      font: "700 10px Arial, sans-serif",
      color: "rgba(231,242,237,.7)"
    });
  }

  function drawReward() {
    if (state.mode !== "reward") return;
    const pulse = 1 + Math.sin(state.elapsed * 4) * 0.018;
    ctx.save();
    ctx.translate(195, 412);
    ctx.scale(pulse, pulse);
    ctx.translate(-195, -412);

    drawText(ctx, "VAULT OPENED", 195, 248, {
      font: "900 10px Arial Black, Arial, sans-serif",
      color: COLORS.mint,
      shadow: "rgba(99,235,179,.8)",
      shadowBlur: 16,
      letterSpacing: 2.8
    });
    drawText(ctx, "ค้นพบรางวัล", 195, 287, {
      font: "700 22px Arial, sans-serif",
      color: "#ffffff"
    });

    ctx.save();
    ctx.shadowColor = COLORS.gold;
    ctx.shadowBlur = 32;
    ctx.fillStyle = "rgba(4,10,8,.78)";
    roundRect(ctx, 84, 322, 222, 151, 34);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,226,151,.66)";
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = COLORS.gold;
    ctx.beginPath();
    ctx.arc(195, 358, 18, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = COLORS.paleGold;
    ctx.lineWidth = 2;
    ctx.stroke();
    drawText(ctx, "888", 195, 358, {
      font: "900 8px Arial Black, Arial, sans-serif",
      color: "#2a1c08"
    });
    drawText(ctx, "+88", 195, 407, {
      font: "900 46px Arial Black, Arial, sans-serif",
      color: COLORS.paleGold,
      shadow: "rgba(214,169,77,.7)",
      shadowBlur: 24
    });
    drawText(ctx, "เครดิตตัวอย่าง", 195, 446, {
      font: "700 12px Arial, sans-serif",
      color: "#dcece5",
      letterSpacing: 1
    });
    ctx.restore();

    const button = ctx.createLinearGradient(0, 641, 0, 701);
    button.addColorStop(0, "#97e2c4");
    button.addColorStop(1, "#348c69");
    ctx.fillStyle = button;
    roundRect(ctx, 67, 636, 256, 62, 19);
    ctx.fill();
    ctx.strokeStyle = "rgba(219,255,240,.8)";
    ctx.stroke();
    drawText(ctx, "เปิดอีกครั้ง", 195, 667, {
      font: "900 16px Arial Black, Arial, sans-serif",
      color: "#07130f"
    });
    drawText(
      ctx,
      "ผลนี้ใช้สำหรับพรีวิวเท่านั้น ไม่มีการเพิ่มเครดิตจริง",
      195,
      731,
      {
        font: "400 9px Arial, sans-serif",
        color: "rgba(207,222,216,.68)"
      }
    );
  }

  function render() {
    if (destroyed) return;
    ctx.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);
    ctx.fillStyle = COLORS.ink;
    ctx.fillRect(0, 0, viewport.width, viewport.height);
    ctx.save();
    ctx.translate(viewport.offsetX, viewport.offsetY);
    ctx.scale(viewport.scale, viewport.scale);

    drawBackdrop();
    drawBrand();
    if (state.mode !== "intro") drawCodeRail();
    drawDoor();
    drawParticles();
    drawInstruction();
    drawOpeningStatus();
    drawPreviewTag();
    if (state.mode === "intro") drawIntro();
    if (state.mode === "reward") drawReward();

    ctx.restore();
  }

  function onPointerDown(event) {
    event.preventDefault();
    audio.unlock();
    const point = toWorld(event.clientX, event.clientY);
    state.pointerDown = true;
    if (canvas.setPointerCapture) {
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch (error) {
        // Older embedded webviews may not support pointer capture.
      }
    }

    if (state.mode === "intro" && pointInRect(point, 50, 598, 290, 118)) {
      startGame();
      return;
    }
    if (state.mode === "reward" && pointInRect(point, 50, 610, 290, 112)) {
      resetGame();
      return;
    }
    if (state.mode === "cracking" && pointInCircle(point, 195, 424, 122)) {
      state.draggingDial = true;
      state.previousPointerAngle = Math.atan2(point.y - 424, point.x - 195);
      return;
    }
    if (state.mode === "unlocked" && pointInRect(point, 300, 260, 88, 330)) {
      state.draggingLever = true;
      state.leverProgress = clamp((point.y - 330) / 190, 0, 1);
    }
  }

  function onPointerMove(event) {
    if (!state.pointerDown) return;
    const point = toWorld(event.clientX, event.clientY);
    if (state.draggingDial && state.mode === "cracking") {
      const angle = Math.atan2(point.y - 424, point.x - 195);
      const delta = normalizeAngle(angle - state.previousPointerAngle);
      state.previousPointerAngle = angle;
      updateDialValue(delta);
    }
    if (state.draggingLever && state.mode === "unlocked") {
      state.leverProgress = clamp((point.y - 330) / 190, 0, 1);
      if (state.leverProgress >= 0.82) beginOpening();
    }
  }

  function onPointerUp(event) {
    state.pointerDown = false;
    state.draggingDial = false;
    if (state.draggingLever && state.mode === "unlocked") {
      state.leverProgress *= 0.4;
    }
    state.draggingLever = false;
    if (canvas.releasePointerCapture && event && event.pointerId != null) {
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Pointer may already be released by the host webview.
      }
    }
  }

  function onWheel(event) {
    if (state.mode !== "cracking") return;
    event.preventDefault();
    updateDialValue(event.deltaY > 0 ? 0.035 : -0.035);
  }

  function onKeyDown(event) {
    const key = event.key.toLowerCase();
    if (key === "f") {
      event.preventDefault();
      if (
        !document.fullscreenElement &&
        canvas.parentElement.requestFullscreen
      ) {
        canvas.parentElement.requestFullscreen();
      } else if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      }
      return;
    }
    if (key === "r") {
      resetGame();
      return;
    }
    if (state.mode === "intro" && (key === "enter" || key === " ")) {
      event.preventDefault();
      startGame();
      return;
    }
    if (state.mode === "reward" && (key === "enter" || key === " ")) {
      event.preventDefault();
      resetGame();
      return;
    }
    if (state.mode === "cracking") {
      if (key === "arrowright") {
        event.preventDefault();
        updateDialValue(TAU / 100);
      } else if (key === "arrowleft") {
        event.preventDefault();
        updateDialValue(-TAU / 100);
      }
      return;
    }
    if (state.mode === "unlocked" && (key === "arrowdown" || key === " ")) {
      event.preventDefault();
      state.leverProgress = clamp(state.leverProgress + 0.18, 0, 1);
      if (state.leverProgress >= 0.82) beginOpening();
    }
  }

  function frame(timestamp) {
    if (destroyed) return;
    const delta = (timestamp - previousTimestamp) / 1000;
    previousTimestamp = timestamp;
    update(delta);
    render();
    animationFrame = requestAnimationFrame(frame);
  }

  function renderGameToText() {
    const target = nextTarget();
    const direction = nextDirection();
    const interactive =
      state.mode === "intro"
        ? "tap start button at x=61..329, y=625..691"
        : state.mode === "cracking"
        ? "drag dial centered at x=195,y=424,r=122; keyboard Left/Right also rotates"
        : state.mode === "unlocked"
        ? "drag lever downward at x=300..388,y=260..590; keyboard Down also pulls"
        : state.mode === "reward"
        ? "tap replay at x=67..323,y=636..698"
        : "vault door is opening; wait for reward";
    return JSON.stringify({
      coordinateSystem:
        "logical portrait canvas 390x844; origin top-left; x increases right, y increases down",
      mode: state.mode,
      status: state.status,
      dial: {
        value: state.dialValue,
        angleRadians: Number(state.dialAngle.toFixed(3)),
        requiredTarget:
          typeof target === "number" ? String(target).padStart(2, "0") : null,
        requiredDirection:
          typeof direction === "number"
            ? direction > 0
              ? "clockwise"
              : "counterclockwise"
            : null
      },
      foundCodes: state.foundCodes.map(code => String(code).padStart(2, "0")),
      leverProgress: Number(state.leverProgress.toFixed(2)),
      doorProgress: Number(state.doorProgress.toFixed(2)),
      reward: state.mode === "reward" ? state.reward : null,
      interactive
    });
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("resize", resize);

  window["render_game_to_text"] = renderGameToText;
  window.advanceTime = milliseconds => {
    const seconds = Math.max(0, Number(milliseconds) || 0) / 1000;
    const steps = Math.max(1, Math.ceil(seconds / (1 / 60)));
    const step = seconds / steps;
    for (let index = 0; index < steps; index += 1) update(step);
    render();
    return Promise.resolve();
  };

  resize();
  animationFrame = requestAnimationFrame(frame);

  return {
    resize,
    renderGameToText,
    destroy() {
      destroyed = true;
      cancelAnimationFrame(animationFrame);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerUp);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", resize);
      if (window["render_game_to_text"] === renderGameToText) {
        delete window["render_game_to_text"];
      }
      audio.destroy();
    }
  };
}
